import {
	Box,
	Chip,
	Collapse,
	Divider,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { type FC, useRef } from "react";
import type { PortfolioListReportingSummaryResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { PortfolioListItemRedesign } from "web_myplann/Portfolio/Components/Redesign/PortfolioListItemRedesign";
import type { CategoryItem } from "web_myplann/Portfolio/Types/portfolioCategory";
import { useScrolledPastTop } from "web_myplann/Shared/Hooks/useScrolledPastTop";

type Props = {
	title: string;
	items: CategoryItem[];
	collapsed: boolean;
	onToggle: () => void;
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined;
};

export const PortfolioCategorySection: FC<Props> = ({
	title,
	items,
	collapsed,
	onToggle,
	summaryData,
}) => {
	const theme = useTheme();

	const sentinelRef = useRef<HTMLDivElement>(null);
	const stuck = useScrolledPastTop(sentinelRef);

	return (
		<Box position="relative" pt={theme.spacingTokens.lg}>
			<Box
				ref={sentinelRef}
				aria-hidden
				position="absolute"
				top={theme.spacing(theme.spacingTokens.lg)}
				height="1px"
				width="100%"
			/>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				onClick={onToggle}
				px={theme.spacingTokens.lg}
				py={theme.spacingTokens.sm}
				bgcolor={theme.palette.grey[50]}
				zIndex={2}
				top={0}
				position="sticky"
				sx={{
					cursor: "pointer",
					"&::after": {
						content: '""',
						position: "absolute",
						inset: 0,
						pointerEvents: "none",
						boxShadow: theme.shadow.stickyHeader,
						opacity: stuck ? 1 : 0,
						transition: theme.transitions.create("opacity", {
							duration: theme.transitions.duration.shorter,
						}),
					},
				}}
			>
				<Stack direction="row" alignItems="center" gap={theme.spacingTokens.sm}>
					<Typography variant="overline" color={theme.palette.grey[500]}>
						{title}
					</Typography>
					<Chip
						variant="tag"
						label={items.length}
						sx={{ color: theme.palette.grey[400] }}
					/>
				</Stack>
				{collapsed ? (
					<ChevronDownIcon
						size={theme.iconSize.sm}
						color={theme.palette.grey[400]}
					/>
				) : (
					<ChevronUpIcon
						size={theme.iconSize.sm}
						color={theme.palette.grey[400]}
					/>
				)}
			</Stack>

			<Collapse in={!collapsed} timeout="auto" unmountOnExit>
				<Box bgcolor="background.paper">
					{items.map((item, index) => {
						const isLast = index === items.length - 1;
						return (
							<Box key={`${item.kind}-${item.data.id}`}>
								<PortfolioListItemRedesign
									item={item}
									summaryData={summaryData}
								/>
								{!isLast && <Divider redesigned />}
							</Box>
						);
					})}
				</Box>
			</Collapse>
		</Box>
	);
};
